import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Badge from '@atlaskit/badge';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { Flex } from '@atlassian/eoc-flex';

import { messages } from './messages';
import { Clickable, ClickableArea, TitleContainer } from './styled';
import { type HeaderProps } from './types';

const Header = ({ actions, isOpen, onOpenChange, title, count }: HeaderProps) => {
	const intlApi = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onToggleCollapse = useCallback(() => {
		// Not allowing collapse if there is no schedule
		if (count) {
			const event = createAnalyticsEvent({});
			fireUIAnalytics(event, 'button clicked', `schedules${isOpen ? 'Closed' : 'Opened'}`);

			onOpenChange(!isOpen);
		}
	}, [count, createAnalyticsEvent, isOpen, onOpenChange]);

	const isDisabled = !count;

	return (
		<Flex alignItems="center">
			<Clickable
				isDisabled={isDisabled}
				onClick={onToggleCollapse}
				tabIndex={0}
				// For A11Y
				aria-expanded={isOpen}
				aria-disabled={isDisabled}
				aria-label={intlApi.formatMessage(messages.schedulesCount, { count })}
				role="button"
				onKeyDown={(e) => {
					if (onToggleCollapse && (e.key === 'Enter' || e.key === ' ')) {
						onToggleCollapse();
					}
				}}
			>
				{isOpen && (
					<ChevronDownIcon
						color="currentColor"
						spacing="spacious"
						testId="opened-section-chevron"
						label=""
					/>
				)}

				{!isOpen && (
					<ChevronRightIcon
						color="currentColor"
						spacing="spacious"
						testId="closed-section-chevron"
						label=""
					/>
				)}

				<TitleContainer>
					<Heading as="h2" size="medium">
						{title}
					</Heading>
					{count && <Badge>{count}</Badge>}
				</TitleContainer>
			</Clickable>

			<ClickableArea isDisabled={!count} onClick={onToggleCollapse} />

			<Flex alignItems="center" justifyContent="flex-end">
				{actions}
			</Flex>
		</Flex>
	);
};

export { Header };
